/**
 * Detects new deployments and notifies users to reload.
 * - Proactive: Polls for deployment changes every 5 minutes
 * - Reactive: Catches chunk load errors and prevents app crash
 */

import environment from '_environments'

const NOTIFICATION_SHOWN_KEY = 'myassetmap_update_notification_shown'
const CURRENT_COMMIT_KEY = 'myassetmap_current_commit'
const CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes
const NOTIFICATION_FLAG_EXPIRY = 24 * 60 * 60 * 1000 // 24 hours

export interface ErrorWithMessage {
  message?: string
  name?: string
}

let pollingStarted = false
let pollingTimerId: NodeJS.Timeout | null = null
let initialCheckTimerId: NodeJS.Timeout | null = null

let isRegistered = false
let currentCleanup: (() => void) | null = null

let notificationDebounceTimer: NodeJS.Timeout | null = null
const NOTIFICATION_DEBOUNCE_MS = 1000

let eventListeners: {
  loadHandler: () => void
  errorHandler: (event: ErrorEvent | PromiseRejectionEvent) => void
  rejectionHandler: (event: ErrorEvent | PromiseRejectionEvent) => void
} | null = null

const getCurrentCommitSHA = (): string | null => {
  return process.env.VERCEL_GIT_COMMIT_SHA ?? null
}

const getStoredCommitSHA = (): string | null => {
  if (typeof sessionStorage === 'undefined') return null
  try {
    return sessionStorage.getItem(CURRENT_COMMIT_KEY)
  } catch {
    return null
  }
}

const storeCurrentCommit = (): void => {
  const commitSHA = getCurrentCommitSHA()
  if (commitSHA && commitSHA !== 'N/A') {
    try {
      sessionStorage.setItem(CURRENT_COMMIT_KEY, commitSHA)
    } catch {
      // Ignore if sessionStorage unavailable
    }
  }
}

const checkForNewDeployment = async (): Promise<boolean> => {
  const storedCommit = getStoredCommitSHA()

  if (!storedCommit || storedCommit === 'N/A') {
    return false
  }

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    let response: Response
    try {
      response = await fetch(window.location.origin + '/index.html', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
    } catch (fetchError) {
      clearTimeout(timeoutId)
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        console.warn('[Deployment] Version check request timed out')
      }
      return false
    }

    if (!response.ok) return false

    const html = await response.text()

    const metaTagMatch = html.match(
      /<meta\s+name=["']vercel-git-commit-sha["']\s+content=["']([^"']+)["']/
    )

    const oldFormatMatch = html.match(
      /VERCEL_GIT_COMMIT_SHA['"]\s*:\s*['"]([^'"]+)['"]/
    )

    const newCommit = metaTagMatch?.[1] ?? oldFormatMatch?.[1]

    if (newCommit && newCommit !== storedCommit && newCommit !== 'N/A') {
      console.log(
        `[Deployment] New version detected! Current: ${storedCommit.substring(0, 7)}, New: ${newCommit.substring(0, 7)}`
      )
      return true
    }

    return false
  } catch (error) {
    console.warn('[Deployment] Failed to check for updates:', error)
    return false
  }
}

const shouldShowNotification = (): boolean => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false
  }

  try {
    const notificationData = sessionStorage.getItem(NOTIFICATION_SHOWN_KEY)

    if (!notificationData) {
      return true
    }

    const { timestamp } = JSON.parse(notificationData)
    const now = Date.now()
    const isExpired = now - timestamp > NOTIFICATION_FLAG_EXPIRY

    if (isExpired) {
      sessionStorage.removeItem(NOTIFICATION_SHOWN_KEY)
      return true
    }

    return false
  } catch {
    return true
  }
}

const markNotificationShown = (): void => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return
  }

  try {
    const notificationData = JSON.stringify({
      timestamp: Date.now(),
    })
    sessionStorage.setItem(NOTIFICATION_SHOWN_KEY, notificationData)
  } catch {
    // Ignore if sessionStorage is unavailable
  }
}

const stopVersionPolling = (): void => {
  if (initialCheckTimerId) {
    clearTimeout(initialCheckTimerId)
    initialCheckTimerId = null
  }
  if (pollingTimerId) {
    clearInterval(pollingTimerId)
    pollingTimerId = null
  }

  // Clear debounce timer on cleanup
  if (notificationDebounceTimer) {
    clearTimeout(notificationDebounceTimer)
    notificationDebounceTimer = null
  }

  pollingStarted = false
}

const triggerUpdateNotification = (): void => {
  if (notificationDebounceTimer) {
    clearTimeout(notificationDebounceTimer)
    notificationDebounceTimer = null
  }

  notificationDebounceTimer = setTimeout(() => {
    notificationDebounceTimer = null

    if (shouldShowNotification()) {
      markNotificationShown()
      window.dispatchEvent(new CustomEvent('app:update-available'))
      stopVersionPolling()
    }
  }, NOTIFICATION_DEBOUNCE_MS)
}

const clearNotificationFlag = (): void => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return
  }

  try {
    sessionStorage.removeItem(NOTIFICATION_SHOWN_KEY)
  } catch {
    // Ignore if sessionStorage is unavailable
  }
}

const startVersionPolling = (): void => {
  if (typeof window === 'undefined') return

  if (environment.isLocal) {
    console.log('[Deployment] Skipping version polling in local development')
    return
  }

  if (pollingStarted) {
    console.warn('[Deployment] Version polling already started, skipping')
    return
  }

  const shouldPoll = getCurrentCommitSHA() && getCurrentCommitSHA() !== 'N/A'
  if (!shouldPoll) return

  pollingStarted = true

  const checkVersion = async () => {
    try {
      const hasUpdate = await checkForNewDeployment()
      if (hasUpdate) {
        triggerUpdateNotification()
      }
    } catch (error) {
      console.warn('[Deployment] Version check failed:', error)
    }
  }

  initialCheckTimerId = setTimeout(() => {
    void checkVersion()
  }, 30000)

  pollingTimerId = setInterval(() => {
    void checkVersion()
  }, CHECK_INTERVAL)
}

export const isChunkLoadError = (error?: ErrorWithMessage | null): boolean => {
  const message = error?.message?.toLowerCase() ?? ''
  const name = error?.name?.toLowerCase() ?? ''

  // Match ONLY failures that are unambiguously about loading an app chunk.
  // We deliberately do NOT match a bare "failed to fetch": that is the browser's
  // generic message for *any* failed fetch() — a dead/slow API, CORS, offline, an
  // expired session, an embedded-iframe 400, etc. Matching it caused an infinite
  // reload loop: a routine API network error was read as "new version available",
  // so the update banner fired; clicking Reload never fixed the underlying failing
  // request, so it fired again, forever. A genuine dynamic-import (chunk) failure
  // instead surfaces as a ChunkLoadError (webpack/rspack) or one of the specific
  // module-loading messages below (native ESM across Chrome/Firefox/Safari).
  return (
    name === 'chunkloaderror' ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('importing a module script failed')
  )
}

const handleError = (event: ErrorEvent | PromiseRejectionEvent): void => {
  const error =
    'reason' in event ? event.reason : 'error' in event ? event.error : null

  if (error && isChunkLoadError(error)) {
    console.warn(
      '[Deployment] Chunk load error detected - new version available',
      error
    )

    if (
      'preventDefault' in event &&
      typeof event.preventDefault === 'function'
    ) {
      event.preventDefault()
    }

    triggerUpdateNotification()
  }
}

if (typeof window !== 'undefined' && !eventListeners) {
  eventListeners = {
    loadHandler: clearNotificationFlag,
    errorHandler: handleError,
    rejectionHandler: handleError,
  }
}

export const registerVersionDetection = (): (() => void) => {
  if (typeof window === 'undefined') return () => {}

  if (isRegistered) {
    console.warn(
      '[Deployment] Version detection already registered. Returning existing cleanup function.'
    )
    return currentCleanup ?? (() => {})
  }

  isRegistered = true

  storeCurrentCommit()

  eventListeners ??= {
    loadHandler: clearNotificationFlag,
    errorHandler: handleError,
    rejectionHandler: handleError,
  }

  window.addEventListener('load', eventListeners.loadHandler)
  window.addEventListener('error', eventListeners.errorHandler, true)
  window.addEventListener('unhandledrejection', eventListeners.rejectionHandler)

  startVersionPolling()

  const cleanup = (): void => {
    if (!isRegistered) return

    if (eventListeners) {
      window.removeEventListener('load', eventListeners.loadHandler)
      window.removeEventListener('error', eventListeners.errorHandler, true)
      window.removeEventListener(
        'unhandledrejection',
        eventListeners.rejectionHandler
      )
    }

    stopVersionPolling()

    isRegistered = false
    currentCleanup = null
  }

  currentCleanup = cleanup

  return cleanup
}
